import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { episodicEventsApplicableForState } from "encamp-waste-accumulation";
import { FacilityWasteView } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { getWasteFacilitiesBreadcrumb } from "util/breadcrumb";
import { ModuleFacilityRouteGate } from "util/facilityProgramEnablement/ModuleFacilityRouteGate";
import { isWasteProgramEffectivelyEnabled } from "util/facilityProgramEnablement/wasteProgramEnablement";

import { useWasteFacility } from "./useWasteFacility";
import { Filter } from "./WasteFacilityFilters";

export type WasteFacilityState = {
  optimisticFilters: Partial<Filter>;
  deferredFilters: Partial<Filter>;
  isPending: boolean;
};

export type WasteFacilityContextType = {
  wasteFacilityState: WasteFacilityState;
  setWasteFacilityState: (cb: (state: Partial<Filter>) => void) => void;
  /** The facility ID to use for waste-related queries (primary facility if colocated) */
  effectiveWasteFacilityId: string;
  /** The primary waste facility info if this facility is colocated, null otherwise */
  colocationWasteFacility: { id: string; name: string } | null | undefined;
  /** Whether this facility's state has adopted subpart L at all (40 CFR 262
   * subpart L) — independent of generator status. Drives whether the waste
   * facility dashboard's episodic-events tile has anything to show. */
  episodicEventsApplicable: boolean;
};

export const WasteFacilityStateContext = createContext<WasteFacilityContextType | undefined>(
  undefined
);

export function useWasteFacilityState() {
  const context = useContext(WasteFacilityStateContext);
  if (!context) {
    throw new Error("useWasteFacilityState must be used within a WasteFacilityStateProvider");
  }
  return context;
}

/**
 * The View filter's default, applied here so every consumer reads the same
 * answer. A URL carrying only a date range leaves `view` unset, and each
 * consumer used to supply its own fallback: some defaulted to All, some passed
 * the blank straight to the server, which also means All. Resolving it once
 * means the dropdown, the grids and the charts cannot disagree about what an
 * unset view means.
 *
 * Reportable rather than All because the facility page's charts draw generator
 * status thresholds, and the generator status calculation counts exactly the
 * lines the Reportable view keeps -- `willNotReport = false` on both the line
 * and its stream (process-manifests/src/calculateGeneratorStatus/getWasteData.ts).
 * All Waste plots waste against thresholds it does not count toward.
 */
export const DEFAULT_WASTE_FACILITY_VIEW = FacilityWasteView.Reportable;

function withDefaultView(filters: Partial<Filter>): Partial<Filter> {
  return filters.view ? filters : { ...filters, view: DEFAULT_WASTE_FACILITY_VIEW };
}

export const WasteFacilityProvider = ({ children }: { children: React.ReactNode }) => {
  const { facilityId, tenantId } = useParams<{ facilityId: string; tenantId: string }>();
  const { data } = useWasteFacility(facilityId);
  const { featureFlags, loading: flagsLoading } = useFeatureFlags();
  const { optimisticFilters, deferredFilters, setFilters, isPending } = useSuspenseQueryParams();

  const wasteFacilityState = useMemo(
    () => ({
      deferredFilters: withDefaultView(deferredFilters),
      optimisticFilters: withDefaultView(optimisticFilters),
      isPending,
    }),
    [deferredFilters, optimisticFilters, isPending]
  );

  // Use the primary facility ID for waste queries if this facility is colocated
  const effectiveWasteFacilityId = data.facility?.colocationWasteFacilityId ?? facilityId ?? "";

  const colocationWasteFacility = data.facility?.colocationWasteFacility;
  const episodicEventsApplicable = episodicEventsApplicableForState(data.facility?.state ?? null);

  const value: WasteFacilityContextType = useMemo(
    () => ({
      wasteFacilityState,
      setWasteFacilityState: setFilters,
      effectiveWasteFacilityId,
      colocationWasteFacility,
      episodicEventsApplicable,
    }),
    [
      wasteFacilityState,
      setFilters,
      effectiveWasteFacilityId,
      colocationWasteFacility,
      episodicEventsApplicable,
    ]
  );

  return (
    <ModuleFacilityRouteGate
      isReady={Boolean(data.facility) && !flagsLoading}
      isEnabled={isWasteProgramEffectivelyEnabled({
        wasteFeatureFlagOverride: data.facility?.wasteFeatureFlagOverride,
        isTenantWasteModuleEnabled: featureFlags[FeatureFlagKey.WasteModule],
      })}
      gridPath={tenantId ? getWasteFacilitiesBreadcrumb(tenantId) : undefined}
    >
      <WasteFacilityStateContext.Provider value={value}>
        {children}
      </WasteFacilityStateContext.Provider>
    </ModuleFacilityRouteGate>
  );
};
